import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { TeamType } from "@redotech/account-common/team-type";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import BoxIcon from "@redotech/redo-icons/box-01.svg";
import BoxFilledIcon from "@redotech/redo-icons/box_filled.svg";
import CallBellIcon from "@redotech/redo-icons/call-bell.svg";
import CallBellFilledIcon from "@redotech/redo-icons/call-bell_filled.svg";
import ChartBarsTrendUpIcon from "@redotech/redo-icons/chart-bars-trend-up.svg";
import ChartBarsTrendUpFilledIcon from "@redotech/redo-icons/chart-bars-trend-up_filled.svg";
import ListIcon from "@redotech/redo-icons/list.svg";
import ListFilledIcon from "@redotech/redo-icons/list_filled.svg";
import PackageSearchIcon from "@redotech/redo-icons/package-search.svg";
import PackageSearchFilledIcon from "@redotech/redo-icons/package-search_filled.svg";
import SearchIcon from "@redotech/redo-icons/search-md.svg";
import SearchFilledIcon from "@redotech/redo-icons/search-md_filled.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import TagFilledIcon from "@redotech/redo-icons/tag-01_filled.svg";
import WAREHOUSE_ICON from "@redotech/redo-icons/warehouse.svg";
import WAREHOUSE_ICON_FILLED from "@redotech/redo-icons/warehouse_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { useMatch } from "react-router-dom";
import { ReplenishmentReportItem } from "./replenishment-report";
import { WorkOrdersItem } from "./work-orders";

export const WarehouseItem = memo(function WarehouseItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const wmsEnabled = useCapability("wms");
  const canViewSupport = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER,
  );
  const outboundLabelsRpcClient = useContext(
    RedoOutboundLabelsRpcClientContext,
  );
  // Non-WMS teams with lot-tracked products still get the Lots pages —
  // the recall-lookup surface for picker-entered lots (OMS-3723).
  const lotsOnlyLoad = useLoad(async () => {
    if (wmsEnabled || !outboundLabelsRpcClient) {
      return false;
    }
    return await outboundLabelsRpcClient.hasLotTrackedProducts(null);
  }, [wmsEnabled, outboundLabelsRpcClient]);

  const wmsBaseUrl = `${routes.shippingBaseUrl}/wms`;
  const warehouseMatch = useMatch(`${routes.warehouseBaseUrl}/*`);
  const wmsMatch = useMatch(`${wmsBaseUrl}/*`);
  const anyChildSelected = !!warehouseMatch || !!wmsMatch;

  const is3PL = team.teamType === TeamType.FulfillmentService;
  const showSupport = is3PL && canViewSupport;

  if (!wmsEnabled) {
    if (!lotsOnlyLoad.value) {
      return null;
    }
    return (
      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={anyChildSelected}
        FilledIcon={WAREHOUSE_ICON_FILLED}
        friendlyName={t("Warehouse management", {
          context: "navigation label for the warehouse management product area",
        })}
        id="warehouse-management"
        UnfilledIcon={WAREHOUSE_ICON}
      >
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${wmsBaseUrl}/lots` }}
          FilledIcon={TagFilledIcon}
          friendlyName={t("Lots", {
            context: "warehouse navigation label for inventory lots or batches",
          })}
          id="lots"
          UnfilledIcon={TagIcon}
        />
      </RedoSecondaryNavItem>
    );
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={anyChildSelected}
      FilledIcon={WAREHOUSE_ICON_FILLED}
      friendlyName={t("Warehouse", {
        context: "navigation label for warehouse operations",
      })}
      id="warehouse-management"
      UnfilledIcon={WAREHOUSE_ICON}
    >
      <WarehouseOverviewItem />
      {is3PL && <WarehouseMetricsItem />}
      {is3PL && <InboundReceivingItem />}
      <BinsItem />
      <InventoryLedgerItem />
      <LotsItem />
      <ReplenishmentReportItem />
      <WarehouseServiceRequestsItem />
      {showSupport && <WarehouseSupportItem />}
      <WorkOrdersItem />
    </RedoSecondaryNavItem>
  );
});

const WarehouseOverviewItem = memo(function WarehouseOverviewItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.warehouseHref }}
      FilledIcon={WAREHOUSE_ICON_FILLED}
      friendlyName={t("Dashboard", {
        context: "warehouse operations dashboard navigation label",
      })}
      id="warehouse-overview"
      UnfilledIcon={WAREHOUSE_ICON}
    />
  );
});

const InboundReceivingItem = memo(function InboundReceivingItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.shippingBaseUrl}/wms/inbound-receiving`,
      }}
      FilledIcon={PackageSearchFilledIcon}
      friendlyName={t("Inbound receiving", {
        context: "warehouse navigation label for receiving inbound stock",
      })}
      id="inbound-receiving"
      UnfilledIcon={PackageSearchIcon}
    />
  );
});

const BinsItem = memo(function BinsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.shippingBaseUrl}/wms/storage-locations`,
      }}
      FilledIcon={BoxFilledIcon}
      friendlyName={t("Bins", {
        context:
          "WMS navigation label for named or addressable warehouse inventory storage slots or locations, not physical containers or trays",
      })}
      id="bins"
      UnfilledIcon={BoxIcon}
    />
  );
});

const InventoryLedgerItem = memo(function InventoryLedgerItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.shippingBaseUrl}/wms/inventory-level-ledger`,
      }}
      FilledIcon={ListFilledIcon}
      friendlyName={t("Inventory ledger", {
        context: "warehouse navigation label for the stock movement ledger",
      })}
      id="inventory-ledger"
      UnfilledIcon={ListIcon}
    />
  );
});

const LotsItem = memo(function LotsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: `${routes.shippingBaseUrl}/wms/lots` }}
      FilledIcon={TagFilledIcon}
      friendlyName={t("Lots", {
        context: "warehouse navigation label for inventory lots or batches",
      })}
      id="lots"
      UnfilledIcon={TagIcon}
    />
  );
});

const WarehouseMetricsItem = memo(function WarehouseMetricsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const exactMatch = useMatch({ path: routes.warehouseMetricsHref, end: true });
  const nestedMatch = useMatch({
    path: `${routes.warehouseMetricsHref}/*`,
    end: false,
  });

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.warehouseMetricsHref }}
      FilledIcon={ChartBarsTrendUpFilledIcon}
      friendlyName={t("Warehouse metrics", {
        context:
          "warehouse navigation label for inbound, processing, quality, refulfillment, and service-level metrics",
      })}
      id="warehouse-metrics"
      selected={!!exactMatch || !!nestedMatch}
      UnfilledIcon={ChartBarsTrendUpIcon}
    />
  );
});

const WarehouseServiceRequestsItem = memo(
  function WarehouseServiceRequestsItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.shippingBaseUrl}/wms/service-requests`,
        }}
        FilledIcon={CallBellFilledIcon}
        friendlyName={t("Service requests", {
          context:
            "order, fulfillment, claims, and warehouse operations navigation label",
        })}
        id="service-requests"
        UnfilledIcon={CallBellIcon}
      />
    );
  },
);

const WarehouseSupportItem = memo(function WarehouseSupportItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: `${routes.shippingBaseUrl}/wms/support` }}
      FilledIcon={SearchFilledIcon}
      friendlyName={t("Support", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="support"
      UnfilledIcon={SearchIcon}
    />
  );
});
